import {
    object, string, boolean, array,
} from 'yup';

import { getValidationMessage } from 'utils/validation';
import { isQRIban } from 'utils';
import { nameValidationRegExp } from 'widgets/WithdrawalPlan/schemas/schemaValidationHelpers';
import { BankInformationEnum } from '../CaseManagement.types';

export const getBankInformationSchema = ({
    showBeneficiaryInfo = false, isJA, isPOA, isPA, isPK,
} : { showBeneficiaryInfo?: boolean; isJA?: boolean; isPOA?: boolean; isPA?: boolean; isPK?: boolean; }) => {
    const {
        mandatoryError, nameError, numberError,
    } = getValidationMessage();

    const nameValidation = string()
        .required(mandatoryError)
        .test('is-valid', nameError, (val: string) => val !== 'N.A')
        .matches(nameValidationRegExp, {
            message: nameError,
            excludeEmptyString: true,
        });

    const stringValueValidation = string()
        .required(mandatoryError)
        .test('is-valid', mandatoryError, (val: string) => val !== 'N/A');

    const zipCodeValidation = string().matches(/^[0-9]+$/, numberError).nullable(false, mandatoryError).required(mandatoryError);

    const accountValidationSchema = {
        iban: string().required(mandatoryError).nullable(false, mandatoryError),
        esr: string()
            .when('iban', {
                is: (iban) => isQRIban(iban),
                then: string()
                    .required(mandatoryError)
                    .test(
                        'no-spaces-length',
                        '3b.withdrawalPlan.error.esr',
                        (value) => {
                            if (!value) return false;
                            const cleaned = value.replace(/\s+/g, '');

                            return cleaned.length === 27;
                        },
                    ),
                otherwise: string().notRequired(),
            }),
        bankName: stringValueValidation,
        zipCode: zipCodeValidation,
        city: stringValueValidation,
        bankAccountOption: string().required(mandatoryError),
        beneficiaryOption: string().nullable(true)
            .when('bankAccountOption', {
                is: (val) => val !== BankInformationEnum.Value5701 && !isPA && (isJA || isPOA) && !(showBeneficiaryInfo && val === BankInformationEnum.Value5700),
                then: string()
                    .required(mandatoryError),
                otherwise: string().notRequired(),
            }),
        zurichInvestDepotAccount: string().nullable(true)
            .when('bankAccountOption', {
                is: (val) => val === BankInformationEnum.Value5702,
                then: string()
                    .required(mandatoryError),
                otherwise: string().notRequired(),
            }),
        messageText1: string(),
        messageText2: string(),
        depotTitle: string(),
        subscriptionFeeConsideration: array()
            .of(boolean())
            .when('bankAccountOption', {
                is: (val) => val === BankInformationEnum.Value5702 && isPK,
                then: array()
                    .of(boolean())
                    .required(mandatoryError),
                otherwise: array()
                    .of(boolean()).notRequired(),
            }),
    };

    if (showBeneficiaryInfo) {
        return object(accountValidationSchema).shape({
            firstName: nameValidation,
            lastName: nameValidation,
            address: string(),
            zipCodeOfBeneficiary: zipCodeValidation,
            cityOfBeneficiary: stringValueValidation,
        });
    }

    return object(accountValidationSchema);
};
